import type {
  AllocationDefinition,
  Command,
  JsonValue,
  RecordDefinition,
  Transaction,
} from "@e308/core";
import { activeBorderIds, rejectUnlessActiveBorder } from "./border-guards.js";
import { combatCommandFor } from "./combat-commands.js";
import { rejectIfEliminated } from "./command-guards.js";
import type { EconomySettings, SeatModel } from "./content-types.js";
import { validatePercentages, validatePriority } from "./definition-validation.js";
import { diplomacyCommandFor } from "./diplomacy-commands.js";
import type { DiplomacyState, MatchLogPayload } from "./diplomacy-types.js";
import { METAL_LANES, POWER_DEPARTMENTS } from "./economy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import type { LockedMatchSetup } from "./lifecycle-state.js";
import type { MatchLifecycleState } from "./lifecycle-types.js";
import { isLogisticsIntent, logisticsCommandFor } from "./logistics-commands.js";
import {
  cancelOrder,
  enqueueOrder,
  reorderQueue,
  setOrderRepeat,
  setQueuePaused,
  withRetoolDeadline,
} from "./manufacturing-state.js";
import type { ManufacturingState } from "./manufacturing-types.js";
import { effectsForResearch } from "./research-effects.js";
import { cancelResearch, enqueueResearch } from "./research-state.js";
import type { ConflictIntent } from "./types.js";

export function commandFor(
  model: SeatModel,
  intent: ConflictIntent,
  seatById: ReadonlyMap<string, SeatModel>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
  diplomacy: RecordDefinition<DiplomacyState & JsonValue>,
  matchLog: import("@e308/core").DomainEventDefinition<MatchLogPayload & JsonValue>,
  setup: LockedMatchSetup,
  stepMs: number,
): Command<Micro> {
  if (intent.kind === "set-power")
    return allocationCommand(model, model.powerOperation, {
      industry: intent.industry,
      defenses: intent.defenses,
      "military-production": intent.militaryProduction,
      research: intent.research,
    });
  if (intent.kind === "set-factory-works")
    return allocationCommand(
      model,
      model.factoryWorksSplit,
      {
        "rig-factory": intent["rig-factory"],
        "yard-factory": intent["yard-factory"],
        "robot-factory": intent["robot-factory"],
      },
      true,
    );
  if (intent.kind === "set-construction-split")
    return allocationCommand(model, model.constructionSplit, {
      "new-construction": intent.newConstruction,
      restoration: intent.restoration,
    });
  if (intent.kind === "set-power-priority") {
    validatePriority(intent.priority, POWER_DEPARTMENTS);
    return priorityCommand(model, { powerPriority: intent.priority });
  }
  if (intent.kind === "set-metal-priority") {
    validatePriority(intent.priority, METAL_LANES);
    return priorityCommand(model, { metalPriority: intent.priority });
  }
  if (intent.kind === "enqueue-queue-order") return enqueueManufacturingCommand(model, intent);
  if (intent.kind === "cancel-queue-order")
    return manufacturingCommand(model, (state) => cancelOrder(state, intent.queue, intent.orderId));
  if (intent.kind === "reorder-queue")
    return manufacturingCommand(model, (state) =>
      reorderQueue(state, intent.queue, intent.orderIds),
    );
  if (intent.kind === "set-queue-repeat")
    return manufacturingCommand(model, (state) =>
      setOrderRepeat(state, intent.queue, intent.orderId, intent.repeat),
    );
  if (intent.kind === "set-queue-paused")
    return manufacturingCommand(model, (state) =>
      setQueuePaused(state, intent.queue, intent.paused),
    );
  if (intent.kind === "emergency-restoration-preset") return emergencyPresetCommand(model);
  if (intent.kind === "enqueue-research" || intent.kind === "cancel-research")
    return researchIntentCommand(model, intent);
  if (intent.kind === "set-supply")
    return borderAllocationCommand(model, model.supplyAllocation, intent.allocations, lifecycle);
  if (intent.kind === "set-shields")
    return borderAllocationCommand(model, model.shieldAllocation, intent.allocations, lifecycle);
  if (intent.kind === "set-infrastructure-target")
    return combatCommandFor(model, intent, seatById, lifecycle, setup);
  if (isLogisticsIntent(intent))
    return logisticsCommandFor(model, intent as never, stepMs, seatById, lifecycle, setup);
  if (intent.kind === "surrender") {
    if (intent.confirmed !== true) throw new TypeError("Surrender must be confirmed");
    return surrenderCommand(model);
  }
  if (isDiplomacyIntent(intent)) {
    const target = seatById.get(intent.targetSeatId);
    if (!target) throw new TypeError(`Unknown target: ${intent.targetSeatId}`);
    return diplomacyCommandFor(model, intent, target, lifecycle, diplomacy, matchLog);
  }
  const target = seatById.get(intent.targetSeatId);
  if (!target) throw new TypeError(`Unknown target: ${intent.targetSeatId}`);
  if (setup.teamBySeat[model.seat.id] === setup.teamBySeat[target.seat.id])
    throw new TypeError("Teammates cannot target each other");
  return targetCommand(model, target, lifecycle);
}

function researchIntentCommand(
  model: SeatModel,
  intent: Extract<ConflictIntent, { readonly kind: "enqueue-research" | "cancel-research" }>,
): Command<Micro> {
  return researchCommand(model, (transaction) => {
    if (intent.kind === "enqueue-research") {
      transaction.setRecord(
        model.research,
        enqueueResearch(transaction.getRecord(model.research), intent.researchKind, intent.target),
      );
      return;
    }
    const canceled = cancelResearch(transaction.getRecord(model.research), intent.orderId);
    transaction.setRecord(model.research, canceled.state);
    transaction.add(model.resources.researchPoints, microFromString(canceled.refund));
  });
}

function isDiplomacyIntent(
  intent: ConflictIntent,
): intent is Extract<
  ConflictIntent,
  { readonly kind: "propose-pact" | "accept-pact" | "end-pact" | "send-metal" }
> {
  return ["propose-pact", "accept-pact", "end-pact", "send-metal"].includes(intent.kind);
}

function enqueueManufacturingCommand(
  model: SeatModel,
  intent: Extract<ConflictIntent, { readonly kind: "enqueue-queue-order" }>,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:enqueue-manufacturing`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      if (
        intent.queue !== "restoration" &&
        !effectsForResearch(transaction.getRecord(model.research)).unlocks.has(intent.target)
      )
        transaction.reject({ code: "locked", prerequisiteIds: [intent.target] });
      transaction.setRecord(
        model.manufacturing,
        enqueueOrder(
          transaction.getRecord(model.manufacturing),
          intent.queue,
          intent.target,
          intent.quantity,
          intent.repeat,
        ),
      );
    },
  };
}

function priorityCommand(model: SeatModel, change: Partial<EconomySettings>): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:set-priority`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      const current = transaction.getRecord(model.economySettings);
      transaction.setRecord(model.economySettings, {
        powerPriority: change.powerPriority ?? current.powerPriority,
        metalPriority: change.metalPriority ?? current.metalPriority,
      });
    },
  };
}

function allocationCommand(
  model: SeatModel,
  allocation: AllocationDefinition<Micro>,
  values: Readonly<Record<string, number>>,
  retool = false,
): Command<Micro> {
  if (
    Object.keys(values).length !== allocation.targets.length ||
    Object.keys(values).some((target) => !allocation.targets.includes(target))
  )
    throw new TypeError("Allocation must contain every target exactly once");
  validatePercentages(values, allocation !== model.powerOperation);
  return {
    id: `intent:${model.seat.id}:${allocation.id}`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      const changed = allocation.targets.some(
        (target) =>
          transaction.getAllocation(allocation.id, target) !==
          microFromString(String(values[target])),
      );
      for (const target of allocation.targets)
        transaction.setAllocation(allocation.id, target, microFromString(String(values[target])));
      if (retool && changed)
        transaction.setRecord(
          model.manufacturing,
          withRetoolDeadline(
            transaction.getRecord(model.manufacturing),
            transaction.gameTimeMs() + 10_000,
          ),
        );
    },
  };
}

function borderAllocationCommand(
  model: SeatModel,
  allocation: AllocationDefinition<Micro>,
  values: Readonly<Record<string, number>>,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
): Command<Micro> {
  validatePercentages(values, true);
  return {
    id: `intent:${model.seat.id}:${allocation.id}`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      const active = activeBorderIds(transaction, model, lifecycle);
      if (
        Object.keys(values).length !== active.length ||
        Object.keys(values).some((id) => !active.includes(id))
      )
        transaction.reject({ code: "invalid-target", id: "border-allocation" });
      for (const id of active)
        transaction.setAllocation(allocation.id, id, microFromString(String(values[id])));
    },
  };
}

function manufacturingCommand(
  model: SeatModel,
  update: (state: ManufacturingState) => ManufacturingState & JsonValue,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:manufacturing`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      transaction.setRecord(
        model.manufacturing,
        update(transaction.getRecord(model.manufacturing)),
      );
    },
  };
}

function researchCommand(
  model: SeatModel,
  update: (transaction: Transaction<Micro>) => void,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:research`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      update(transaction);
    },
  };
}

function emergencyPresetCommand(model: SeatModel): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:emergency-restoration-preset`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      let state = transaction.getRecord(model.manufacturing);
      for (const target of ["mining-rig", "power-generator", "construction-yard"] as const) {
        const wreckage = transaction.get(model.resources.wreckage[target]);
        const queue = wreckage > 0n ? "restoration" : "construction";
        state = enqueueOrder(
          state,
          queue,
          target,
          wreckage > 0n ? formatMicro(wreckage) : "1",
          false,
        );
      }
      transaction.setRecord(model.manufacturing, state);
    },
  };
}

function surrenderCommand(model: SeatModel): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:surrender`,
    execute: (transaction) => {
      if (transaction.get(model.resources.headquarters) <= 0n) return;
      transaction.set(model.resources.headquarters, microFromString("0"));
    },
  };
}

function targetCommand(
  model: SeatModel,
  target: SeatModel,
  lifecycle: RecordDefinition<MatchLifecycleState & JsonValue>,
): Command<Micro> {
  return {
    id: `intent:${model.seat.id}:set-target`,
    execute: (transaction) => {
      rejectIfEliminated(transaction, model);
      rejectUnlessActiveBorder(transaction, model, target, lifecycle);
      for (const id of model.borderIds)
        transaction.setAllocation(
          model.targeting.id,
          id,
          microFromString(id === target.seat.id ? "100" : "0"),
        );
    },
  };
}
